import React, { useState, useRef } from 'react';

const EnhancedJournaling = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [currentPrompt, setCurrentPrompt] = useState(0);
  const mediaRecorder = useRef(null);
  const [recordingStatus, setRecordingStatus] = useState('');
  const chunks = useRef([]);

  const guidedPrompts = [
    "What sights, sounds, or smells do you remember from this moment?",
    "How were you feeling during this memory?",
    "Who was there with you?",
    "What makes this memory special to you?",
    "What were you wearing or doing?",
    "What was happening around you?",
    "What thoughts were going through your mind?"
  ];

  const startRecording = async () => {
    try {
      chunks.current = []; // Reset chunks
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);
      
      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.current.push(event.data);
        }
      };

      mediaRecorder.current.onstop = async () => {
        const audioBlob = new Blob(chunks.current, { type: 'audio/webm' });
        setRecordingStatus('Converting speech to text...');
        
        try {
          // Create FormData and append audio file
          const formData = new FormData();
          formData.append('audio', audioBlob, 'recording.webm');

          // If you have a speech-to-text API endpoint:
          // const response = await fetch('your-speech-to-text-api', {
          //   method: 'POST',
          //   body: formData
          // });
          // const data = await response.json();
          // setTranscript(prev => prev + "\n• " + data.text);

          // For testing, we'll use the Web Speech API
          const text = await transcribeAudio(audioBlob);
          setTranscript(prev => prev + "\n• " + text);
          setRecordingStatus('');
        } catch (error) {
          console.error('Transcription error:', error);
          setRecordingStatus('Error converting speech to text');
        }
      };

      mediaRecorder.current.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingStatus('Recording...');
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setRecordingStatus('Error: Could not access microphone');
    }
  };

  const transcribeAudio = async (audioBlob) => {
    return new Promise((resolve) => {
      const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
      recognition.lang = 'en-US';
      recognition.continuous = true;
      recognition.interimResults = false;

      let finalTranscript = '';

      recognition.onresult = (event) => {
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          }
        }
      };

      recognition.onend = () => {
        resolve(finalTranscript || 'No speech detected. Please try again.');
      };

      // Convert blob to audio element and play it through recognition
      const audio = new Audio(URL.createObjectURL(audioBlob));
      audio.addEventListener('ended', () => recognition.stop());
      
      recognition.start();
      audio.play();
    });
  };

  const stopRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop();
      mediaRecorder.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  const nextPrompt = () => {
    setCurrentPrompt((prev) => (prev + 1) % guidedPrompts.length);
  };

  const saveMemory = () => {
    if (transcript.trim()) {
      // Get existing memories
      const memories = JSON.parse(localStorage.getItem('memories') || '[]');
      
      // Create new memory
      const newMemory = {
        id: Date.now(),
        title: `Voice Recording ${new Date().toLocaleDateString()}`,
        date: new Date().toISOString(),
        details: transcript,
        type: 'voice',
        createdAt: new Date().toISOString()
      };
      
      // Save to localStorage
      localStorage.setItem('memories', JSON.stringify([...memories, newMemory]));
      
      // Clear transcript
      setTranscript('');
      setRecordingStatus('Memory saved successfully!');
      setTimeout(() => setRecordingStatus(''), 2000);
    }
  };

  return (
    <div className="enhanced-journaling-container">
      <div className="journaling-header">
        <h2>Enhanced Memory Journaling</h2>
        <p className="journaling-description">
          Capture your memories naturally through voice or writing, with helpful prompts 
          to guide your recollection.
        </p>
      </div>

      <div className="journaling-sections">
        <div className="voice-capture-section">
          <h3>Voice Capture</h3>
          <div className="recording-interface">
            <button 
              className={`record-button ${isRecording ? 'recording' : ''}`}
              onClick={isRecording ? stopRecording : startRecording}
            >
              {isRecording ? 'Stop Recording' : 'Start Recording'}
            </button>
            {recordingStatus && (
              <p className="recording-status">{recordingStatus}</p>
            )}
          </div>
        </div>

        <div className="prompt-section">
          <h3>Memory Prompts</h3>
          <div className="prompt-card">
            <p className="current-prompt">{guidedPrompts[currentPrompt]}</p>
            <button className="next-prompt-btn" onClick={nextPrompt}>
              Next Prompt
            </button>
          </div>
        </div>

        <div className="transcription-section">
          <h3>Your Memory</h3>
          <textarea
            className="memory-transcript"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            placeholder="Your recorded memories will appear here. You can also type directly..."
            rows={8}
          />
          <div className="memory-controls">
            <button className="save-memory-btn" onClick={saveMemory}>
              Save Memory
            </button>
            <button className="clear-memory-btn" onClick={() => setTranscript('')}>
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedJournaling; 